import React, { useState, useContext } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Layers,
  Image,
  Info,
  Users,
  BadgeCheck,
  FolderTree,
  Boxes,
  ShieldCheck,
  BriefcaseBusiness,
  FolderKanban,
  MessageSquareText,
  TriangleAlert,
  Handshake,
  Mail,
  Newspaper,
  CalendarDays,
  Layout,
  UserRound,
  Settings,
  Camera,
  LogOut,
  MessageSquare,
  Menu,
  Search,
  Bell,
  X,
  PanelLeftClose,
  ChevronsLeft,
} from "lucide-react";

import { toast } from "react-toastify";
import { AuthContext } from "../../context/authContext";
import { logo } from "../image";

// --- 1. SIDEBAR COMPONENT ---
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success("🛡️ Suraksha 24x7: Signed out");
    navigate("/login");
  };

  const menuItems = [
    { name: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },

    { section: "Layout Settings" },
    // { name: "Navbar Manager", icon: Layers, path: "/admin/header" },
    { name: "Banner Management", icon: Image, path: "/admin/product-banners" },
    { name: "About Section", icon: Info, path: "/admin/about" },
    { name: "Client Section", icon: Users, path: "/admin/client" },
    { name: "Portfolio Section", icon: Users, path: "/admin/portfolio" },

    { section: "Security Products" },
    { name: "Security Products", icon: Boxes, path: "/admin/products" },
    { name: "Security Category", icon: FolderTree, path: "/admin/category" },
    {
      name: "Security Sub Category",
      icon: FolderTree,
      path: "/admin/sub-category",
    },
    { name: "Security Brands", icon: BadgeCheck, path: "/admin/brands" },
    { section: "IT Products" },
    { name: "IT Product Products", icon: Boxes, path: "/admin/it-products" },
    { name: "IT Product Categories", icon: FolderTree, path: "/admin/ip-network-categories" },
    { name: "IT Product Subcategories", icon: FolderTree, path: "/admin/ip-network-subcategories" },
    { name: "IT Product Brands", icon: BadgeCheck, path: "/admin/ip-network-brands" },
    { name: "All Solutions", icon: ShieldCheck, path: "/admin/solutions" },
    { name: "All Services", icon: BriefcaseBusiness, path: "/admin/service" },
    { name: "Projects", icon: FolderKanban, path: "/admin/project" },
    {
      name: "Product Inquiries",
      icon: MessageSquareText,
      path: "/admin/product-inquiry",
    },
    { name: "Complaints", icon: TriangleAlert, path: "/admin/complaints" },
    // { name: "Partner", icon: Handshake, path: "/admin/partners" },
    { name: "Contacts", icon: Mail, path: "/admin/contact" },
    { name: "Blog", icon: Newspaper, path: "/admin/blogs" },
    { name: "Reviews", icon: MessageSquare, path: "/admin/reviews" },
    // { name: "Events", icon: CalendarDays, path: "/admin/events" },

    // { section: "Dynamic Content" },
    // { name: "Page Manager", icon: Layout, path: "/admin/dynamic-pages" },

    { section: "System" },
    { name: "Careers", icon: UserRound, path: "/admin/career" },
    { name: "Applications", icon: UserRound, path: "/admin/applications" },
    { name: "SEO", icon: UserRound, path: "/admin/seo" },
  ];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 md:hidden backdrop-blur-sm"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-[999] md:z-0  h-screen w-64 bg-slate-900 border-r border-slate-800 transition-transform duration-300 ease-in-out
${isOpen ? "translate-x-0" : "-translate-x-full"}
md:translate-x-0`}
      >
        <div className="flex items-center justify-between md:justify-center px-5 py-4 border-b border-slate-800">
          <div>
            <h2 className="text-base font-bold text-white ">Suraksha 24/7</h2>
            <p className="text-xs text-slate-500 ">Admin Panel</p>
          </div>

          <button
            onClick={toggleSidebar}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-white md:hidden"
          >
            <ChevronsLeft size={20} />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-140px)] custom-scrollbar">
          {menuItems.map((item, idx) =>
            item.section ? (
              <div
                key={idx}
                className="pt-6 pb-2 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] px-4"
              >
                {item.section}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => window.innerWidth < 768 && toggleSidebar()}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all group ${
                  location.pathname === item.path
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <item.icon
                  size={18}
                  className={
                    location.pathname === item.path
                      ? "text-white"
                      : "group-hover:text-blue-400"
                  }
                />
                <span className="truncate">{item.name}</span>
              </Link>
            ),
          )}

          <div className="pt-4 mt-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 w-full transition-all"
            >
              <LogOut size={18} /> Sign Out
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};

const Topbar = ({ toggleSidebar }) => {
  const { user } = useContext(AuthContext);
  const handleLogout = () => {
    logout();
    toast.success("🛡️ Suraksha 24x7: Signed out");
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-slate-800 bg-slate-900/90 backdrop-blur-md">
      <div className="flex h-full items-center justify-between px-4 md:px-6">
        {/* Left */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleSidebar}
            className="md:hidden rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
          >
            <Menu size={22} />
          </button>
        </div>

        <div className=" items-center gap-4 hidden md:flex">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 w-full transition-all"
          >
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </div>
    </header>
  );
};

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-300 font-sans selection:bg-blue-500/30">
      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />
      <div className="md:pl-64 flex flex-col min-h-screen">
        <Topbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        {/* Content */}
        <main
          className="
      flex-1
      py-3
      px-1

      overflow-x-hidden
    "
        >
          <Outlet />
        </main>

        {/* Footer */}
        <footer
          className="
      p-4
      sm:p-6
      border-t
      border-slate-800/50
      text-[10px]
      text-slate-600
      text-center
    "
        >
          <p
            className="
      uppercase
      tracking-widest
      font-medium
    "
          >
            © 2026 SURAKSHA 24x7 • SMART SURVEILLANCE INFRASTRUCTURE
          </p>
        </footer>
      </div>
    </div>
  );
}
